// header-cart-count-sync.js
//
// Standalone, independent header cart-count updater.
// Does NOT depend on any other script's events being correct.
// It works by:
//   1. Watching every fetch() call made anywhere on the page.
//   2. Whenever a call goes to a known cart-mutating endpoint
//      (/cart/add.js, /cart/change.js, /cart/update.js, /cart/clear.js),
//      it waits for that call to finish, then independently fetches
//      /cart.js itself to get the TRUE current item count.
//   3. It writes that count directly into the header bubble element,
//      bypassing any custom events entirely.

(function () {
  const CART_MUTATION_PATTERNS = [
    '/cart/add',
    '/cart/change',
    '/cart/update',
    '/cart/clear',
  ];

  function isCartMutationUrl(url) {
    if (!url) return false;
    const urlString = typeof url === 'string' ? url : url.toString();
    return CART_MUTATION_PATTERNS.some((pattern) => urlString.includes(pattern));
  }

  function getBubbleEls() {
    // Support multiple cart-icon instances on the page (e.g. mobile + desktop headers)
    return document.querySelectorAll('cart-icon');
  }

  function setCount(itemCount) {
    const bubbles = getBubbleEls();

    bubbles.forEach((cartIcon) => {
      const countEl = cartIcon.querySelector('[ref="cartBubbleCount"], .cart-bubble__text-count');
      const bubbleEl = cartIcon.querySelector('[ref="cartBubble"], .cart-bubble');

      if (countEl) {
        countEl.textContent = itemCount < 100 ? String(itemCount) : '';
        countEl.classList.toggle('hidden', itemCount === 0);
      }

      if (bubbleEl) {
        bubbleEl.classList.toggle('visually-hidden', itemCount === 0);
      }

      cartIcon.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
    });
  }

  async function syncCountFromServer() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      setCount(cart.item_count ?? 0);
    } catch (err) {
      console.warn('[HeaderCartCountSync] Failed to fetch /cart.js:', err);
    }
  }

  // ── Patch window.fetch so we see every cart mutation, no matter which
  //    script on the page triggers it ──────────────────────────────────────
  const originalFetch = window.fetch;

  window.fetch = function (...args) {
    const request = args[0];
    const url = request instanceof Request ? request.url : request;
    const shouldWatch = isCartMutationUrl(url);

    const fetchPromise = originalFetch.apply(this, args);

    if (shouldWatch) {
      fetchPromise
        .then((response) => {
          // Only sync on successful responses
          if (response.ok) {
            // Small delay to let Shopify fully commit the cart change server-side
            setTimeout(syncCountFromServer, 50);
          }
          return response;
        })
        .catch(() => {
          // Ignore — if the mutation failed, no need to sync
        });
    }

    return fetchPromise;
  };

  // ── Also sync once on normal page load, straight from the server-rendered
  //    truth, just to be safe (in case DOM/JS timing ever drifts) ──────────
  function initialSync() {
    // Read directly from the DOM first (fast, no network needed) —
    // the server already rendered the correct count in cart-bubble.liquid.
    // We don't touch it. This script only acts on NEW cart mutations from here on.
  }

  document.addEventListener('DOMContentLoaded', initialSync);

  // ── Handle bfcache restores (back/forward navigation) ────────────────────
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      syncCountFromServer();
    }
  });
})();